import React from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import fruitveg from '../assets/images/fruitveg.jpg';
import healthycooking from '../assets/images/healthycooking.jpg';

function Nutrition() {
  const { t } = useTranslation();
  const checklist = t('nutrition.checklist', { returnObjects: true });

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title={t('seo.nutrition.title')}
        description={t('seo.nutrition.description')}
        path="/nutrition"
      />
      <Navbar />

      <main className="flex-grow max-w-7xl mx-auto px-6 lg:px-12 py-6 lg:py-20 text-left">

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#162E3B] text-center mb-10">
          {t('nutrition.title')}
        </h1>

        {/* Intro section */}
        <div className="space-y-6 mb-12">
          <p className="text-base sm:text-lg text-gray-800 font-semibold leading-relaxed">
            {t('nutrition.intro1')}
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            {t('nutrition.intro2')}
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            {t('nutrition.intro3')}
          </p>
        </div>

        {/* Self-Reflection Card & Fruit/Veg Image */}
        <div className="flex flex-col lg:flex-row gap-10 items-stretch mb-16">
          <div className="lg:w-3/5 flex flex-col justify-between space-y-6">

            {/* Self-Reflection checklist container */}
            <div className="bg-[#162E3B] text-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-base sm:text-lg font-extrabold text-[#FFD82D] mb-6">
                {t('nutrition.checklistTitle')}
              </h3>
              <ul className="space-y-4">
                {checklist.map((item, index) => (
                  <li key={index} className="flex items-start text-base sm:text-lg font-bold">
                    <span className="text-[#FFD82D] mr-3 text-xl">?</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('nutrition.processed1')}
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('nutrition.processed2')}
            </p>
          </div>

          <div className="lg:w-2/5 w-full">
            <img
              className="w-full h-full min-h-[300px] object-cover rounded-2xl shadow-lg border border-gray-100"
              src={fruitveg}
              alt="Assorted fruits and vegetables"
              loading="lazy"
            />
          </div>
        </div>

        {/* You Are What You Eat & Quality-Time-Price */}
        <div className="flex flex-col-reverse lg:flex-row gap-10 items-start mb-16">
          <div className="lg:w-1/3 w-full flex flex-col items-center">
            <img
              className="w-full h-[350px] object-cover rounded-2xl shadow-lg border border-gray-100 mb-4"
              src={healthycooking}
              alt="Healthy cooking preparation"
              loading="lazy"
            />
            <span className="text-lg font-extrabold tracking-widest text-[#162E3B] uppercase">
              {t('nutrition.youAreWhatYouEat')}
            </span>
          </div>

          <div className="lg:w-2/3 space-y-6">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('nutrition.changeHabits')}
            </p>

            {/* Quality, Time, Price callout */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
              <h4 className="text-lg font-extrabold text-[#162E3B] mb-4">
                {t('nutrition.triadTitle')}
              </h4>
              <p className="text-base sm:text-lg text-gray-600 mb-4">
                {t('nutrition.triadIntro')}
              </p>
              <div className="grid grid-cols-3 gap-4 text-center font-bold">
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">{t('nutrition.triadQuality')}</span>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">{t('nutrition.triadTime')}</span>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <span className="text-[#162E3B] text-lg block">{t('nutrition.triadPrice')}</span>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-600 mt-4">
                {t('nutrition.triadOutro')}
              </p>
            </div>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {t('nutrition.fuel')}
            </p>
          </div>
        </div>

        {/* Nutritional Supplements Section */}
        <div className="space-y-6 bg-gray-50 border border-gray-150 p-8 rounded-2xl">
          <h3 className="text-xl sm:text-2xl font-extrabold text-[#162E3B]">
            {t('nutrition.supplementsTitle')}
          </h3>

          <p className="text-base text-gray-700 leading-relaxed">
            {t('nutrition.supplements1')}
          </p>

          <p className="text-base text-gray-700 leading-relaxed">
            {t('nutrition.supplements2')}
          </p>

          <p className="text-base text-gray-700 leading-relaxed font-semibold">
            {t('nutrition.supplements3')}
          </p>

          {/* Highly visible Warning/Disclaimer Alert box */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-xl mt-6">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <p className="text-amber-900 font-extrabold text-lg mb-1">
                  {t('nutrition.noticeTitle')}
                </p>
                <p className="text-amber-850 text-base sm:text-lg leading-relaxed">
                  {t('nutrition.noticeText')}
                </p>
              </div>
            </div>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}

export default Nutrition;
